import React, { useCallback, useMemo } from 'react'
import { StyleSheet, View } from 'react-native'
import { BtLink, BtText } from '@linqpal/components/src/ui'
import { observer } from 'mobx-react'
import { useTranslation } from 'react-i18next'
import { Spacer } from '../../../../../ui/atoms'
import ApplicationStore from '../../ApplicationStore'
import { commonColors } from '@linqpal/common-frontend/src/theme'
import { FlowController, ModelUpdateInfo } from '../../FlowController'
import { getGroupTitle } from '../../groupTitles'
import { LineOfCreditAgreement, VerifyEmailAlert } from '../../components'
import { OwnerReview } from './components/OwnerReview'

function Preview(props) {
  const { t } = useTranslation('application')
  const { doc } = props
  const { showVerifyEmail, setShowVerifyEmail } = ApplicationStore

  const flowController = useMemo(() => new FlowController(doc), [doc])

  const onValueUpdate = useCallback((updateInfo: ModelUpdateInfo) => {
    ApplicationStore.handleDocumentUpdate(
      updateInfo.value,
      updateInfo.identifier,
      updateInfo.group,
      updateInfo.filled,
      getGroupTitle(updateInfo.group) || '',
    )
  }, [])

  const flowSteps = flowController.getFlowSteps()

  return (
    <>
      {flowController.getFlowGroups().map((group) => {
        const section = doc?.data?.get(group)
        if (!section) return null
        else
          return (
            <>
              <Spacer height={25} />
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <BtText style={styles.sectionTitle} key={section?.title}>
                  {t(section?.title)}
                </BtText>
                <BtLink
                  title={t('Review.Edit')}
                  textStyle={styles.linkText}
                  disabled={ApplicationStore.buttonLoading}
                  onPress={() => {
                    flowController.editSection(section.group)
                  }}
                />
              </View>
              <Spacer height={4} />
              {group === 'businessOwner' ? (
                <OwnerReview draft={doc} flowController={flowController} />
              ) : (
                section.items.map((item) => {
                  if (
                    !flowSteps.includes(`${section.group}.${item.identifier}`)
                  )
                    return null
                  const editor = flowController.findComponent(
                    section.group,
                    item.identifier,
                  )
                  if (!editor) return null
                  const { component: Component, decidingQuestion } = editor
                  return (
                    <Component
                      doc={doc}
                      group={section.group}
                      item={item.identifier}
                      decidingQuestion={decidingQuestion}
                      onValueUpdate={onValueUpdate}
                      flowController={flowController}
                      review
                    />
                  )
                })
              )}
            </>
          )
      })}
      <VerifyEmailAlert close={showVerifyEmail} setClose={setShowVerifyEmail} />
      {ApplicationStore.isCreditApplication && <LineOfCreditAgreement />}
    </>
  )
}

export default {
  title: 'Preview.Heading',
  canMoveNext: false,
  canSkip: false,
  component: observer(Preview),
  showFooterMessage: true,
}

const styles = StyleSheet.create({
  sectionTitle: {
    fontWeight: '600',
    fontSize: 20,
    lineHeight: 26,
  },
  linkText: {
    color: commonColors.accentText,
    fontWeight: '600',
    fontSize: 16,
    lineHeight: 24,
  },
})
